"use client"

import { useState, useEffect } from "react"
import { useRouter } from "next/navigation"
import Link from "next/link"
import Image from "next/image"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { TurnstileWidget } from "@/components/turnstile-widget"
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field"
import { PhoneNumberInput } from "@/components/phone-number-input"
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
} from "@/components/ui/input-otp"
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs"
import { useLandingTranslations } from "@/hooks/useLandingTranslations"
import { LandingLanguageSwitcher } from "@/components/landing-language-switcher"
import { setAuthSession, setAuthSessionByUserId, clearDeveloperSession } from "@/lib/auth"
import { normalizeLocale, denormalizeLocale } from "@/i18n/config"
import { normalizePhoneNumber } from "@/lib/utils"
import {
  getDefaultCountryCodeByLocale,
  type PhoneCountryCode,
} from "@/lib/phone"
import type { Locale } from "@/i18n/config"
import { Spinner } from "@/components/ui/spinner"
import { alternateSendViaSmsForResend } from "@/lib/otpPublicChannel"

export type SigninFormProps = React.ComponentProps<"div"> & {
  onLoginSuccessWithoutRedirect?: (data: { phone: string; userId: string }) => void;
  /** Hide identifier/password tab — WhatsApp OTP only (e.g. affiliate panel). */
  whatsappOnly?: boolean;
  title?: string;
  subtitle?: React.ReactNode;
};

export function SigninForm({
  className,
  onLoginSuccessWithoutRedirect,
  whatsappOnly = false,
  title,
  subtitle,
  ...props
}: SigninFormProps) {
  const [phone, setPhone] = useState("")
  const { t, locale } = useLandingTranslations()
  const [countryCode, setCountryCode] = useState<PhoneCountryCode>(() =>
    getDefaultCountryCodeByLocale(locale)
  )

  useEffect(() => {
    setCountryCode(getDefaultCountryCodeByLocale(locale))
  }, [locale])

  const [otpCode, setOtpCode] = useState("")
  const [step, setStep] = useState<"phone" | "otp">("phone")
  const [isLoading, setIsLoading] = useState(false)
  const [isSendingOTP, setIsSendingOTP] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null)
  const [didNotReceiveCooldownSec, setDidNotReceiveCooldownSec] = useState(0)

  // Identifier tab state
  const [identifier, setIdentifier] = useState("")
  const [password, setPassword] = useState("")
  const [identifierLoading, setIdentifierLoading] = useState(false)
  const [identifierError, setIdentifierError] = useState<string | null>(null)
  const [identifierTurnstileToken, setIdentifierTurnstileToken] = useState<string | null>(null)

  const router = useRouter()

  useEffect(() => {
    if (didNotReceiveCooldownSec <= 0) return
    const id = window.setInterval(() => {
      setDidNotReceiveCooldownSec((s) => (s <= 1 ? 0 : s - 1))
    }, 1000)
    return () => window.clearInterval(id)
  }, [didNotReceiveCooldownSec])

  const turnstileSiteKey = process.env.NEXT_PUBLIC_CLOUDFLARE_TURNSTILE_SITE_KEY ?? ""
  const turnstileRequired =
    Boolean(turnstileSiteKey?.trim()) && process.env.NODE_ENV !== 'development'

  const getNormalizedPhone = () => {
    const localPhone = phone.trim()
    if (!localPhone) {
      throw new Error(t("checkout.validation.phoneRequired"))
    }
    const normalizedLocale = denormalizeLocale(locale) as Locale
    return normalizePhoneNumber(`${countryCode}${localPhone}`, normalizedLocale)
  }

  const handleSendOTP = async (
    e: React.FormEvent | React.MouseEvent | null,
    sendViaSms?: boolean
  ) => {
    e?.preventDefault()
    setError(null)
    setIsSendingOTP(true)

    try {
      const normalizedPhone = getNormalizedPhone()

      if (turnstileRequired && !turnstileToken?.trim()) {
        setError(t("signin.turnstileRequired") ?? "Please complete the verification below.")
        setIsSendingOTP(false)
        return
      }

      const response = await fetch("/api/auth/send-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          phone: normalizedPhone,
          locale: locale,
          ...(sendViaSms !== undefined && { sendViaSms }),
          ...(turnstileToken?.trim() && { turnstileToken: turnstileToken.trim() }),
        }),
      })

      const data = await response.json()

      if (!response.ok) {
        let errorMessage = t('signin.error')
        if (response.status === 429 && typeof data.rateLimitRemaining === 'number') {
          errorMessage = t('otp.rateLimit', { seconds: data.rateLimitRemaining })
        } else if (data.error) {
          if (data.error.includes('Invalid phone number') || data.error.includes('Access denied')) {
            errorMessage = t('signin.invalidPhone')
          } else if (data.error.includes('Server configuration error')) {
            errorMessage = t('signin.serverError')
          } else if (data.error.includes('required')) {
            errorMessage = t('checkout.validation.phoneRequired')
          } else {
            errorMessage = data.error
          }
        }
        throw new Error(errorMessage)
      }

      setTurnstileToken(null)
      setDidNotReceiveCooldownSec(30)
      setStep("otp")
    } catch (err) {
      setError(err instanceof Error ? err.message : t('signin.error'))
    } finally {
      setIsSendingOTP(false)
    }
  }

  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setIsLoading(true)

    try {
      if (otpCode.length !== 6) {
        throw new Error(t('otp.invalidCode'))
      }

      const normalizedPhone = getNormalizedPhone()

      const response = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone: normalizedPhone, code: otpCode }),
      })

      const data = await response.json()

      if (!response.ok) {
        let errorMessage = t('signin.error')
        if (data.error) {
          if (data.error.includes('Invalid code') || data.error.includes('expired')) {
            errorMessage = data.error
          } else if (data.error.includes('Invalid phone number') || data.error.includes('Access denied')) {
            errorMessage = t('signin.invalidPhone')
          } else if (data.error.includes('Server configuration error')) {
            errorMessage = t('signin.serverError')
          } else {
            errorMessage = data.error
          }
        }
        throw new Error(errorMessage)
      }

      if (data.phone) {
        await clearDeveloperSession()
        setAuthSession(data.phone)
      }

      if (onLoginSuccessWithoutRedirect && data.userId && data.phone) {
        onLoginSuccessWithoutRedirect({ phone: data.phone, userId: data.userId })
        setIsLoading(false)
        return
      }

      const normalizedLocaleForRoute = normalizeLocale(locale)
      router.push(`/${normalizedLocaleForRoute}/panel`)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('signin.error'))
      setIsLoading(false)
    }
  }

  const handleBackToPhone = () => {
    setStep("phone")
    setPhone("")
    setCountryCode(getDefaultCountryCodeByLocale(locale))
    setOtpCode("")
    setTurnstileToken(null)
    setDidNotReceiveCooldownSec(0)
    setError(null)
  }

  const handleIdentifierLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setIdentifierError(null)
    setIdentifierLoading(true)

    if (turnstileRequired && !identifierTurnstileToken?.trim()) {
      setIdentifierError(t("signin.turnstileRequired") ?? "Please complete the verification below.")
      setIdentifierLoading(false)
      return
    }

    try {
      const response = await fetch("/api/auth/identifier-login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ identifier: identifier.trim(), password, ...(identifierTurnstileToken?.trim() && { turnstileToken: identifierTurnstileToken.trim() }) }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error ?? t('signin.identifierError'))
      }

      await clearDeveloperSession()
      setAuthSessionByUserId(data.userId)

      if (onLoginSuccessWithoutRedirect && data.userId && data.phone) {
        onLoginSuccessWithoutRedirect({ phone: data.phone, userId: data.userId })
        setIdentifierLoading(false)
        return
      }

      const normalizedLocaleForRoute = normalizeLocale(locale)
      router.push(`/${normalizedLocaleForRoute}/panel`)
    } catch (err) {
      setIdentifierError(err instanceof Error ? err.message : t('signin.identifierError'))
      setIdentifierLoading(false)
    }
  }

  return (
    <div className={cn("flex flex-col gap-6", className)} {...props}>
      <div className="flex flex-col items-center gap-2 text-center">
        <Link href="/" className="flex flex-col items-center gap-2 font-medium">
          <div className="flex size-12 items-center justify-center rounded-md">
            <Image
              src="/logo.png"
              alt="Vupt Market"
              width={48}
              height={48}
              className="object-contain"
            />
          </div>
          <span className="sr-only">Vupt Market</span>
        </Link>
        <h1 className="text-xl font-bold">{title ?? t('signin.title')}</h1>
        <FieldDescription>
          {subtitle ?? (
            <>
              {t('signin.subtitle')}{" "}
              <span className="font-medium">{t('signin.signUp')}</span>
            </>
          )}
        </FieldDescription>
      </div>

      <Tabs defaultValue="whatsapp" className="w-full">
        {!whatsappOnly && (
          <TabsList className="w-full">
            <TabsTrigger value="whatsapp" className="flex-1">
              {t('signin.tabWhatsApp')}
            </TabsTrigger>
            <TabsTrigger value="identifier" className="flex-1">
              {t('signin.tabIdentifier')}
            </TabsTrigger>
          </TabsList>
        )}

        {/* WhatsApp / OTP tab */}
        <TabsContent value="whatsapp">
          <form onSubmit={step === "phone" ? handleSendOTP : handleVerifyOTP}>
            <FieldGroup>
              {error && (
                <div className="rounded-md bg-destructive/15 p-3 text-sm text-destructive">
                  {error}
                </div>
              )}

              {step === "phone" ? (
                <>
                  <Field>
                    <FieldLabel htmlFor="phone">{t('signin.phoneNumber')}</FieldLabel>
                    <PhoneNumberInput
                      id="phone"
                      value={phone}
                      onChange={setPhone}
                      countryCode={countryCode}
                      onCountryCodeChange={setCountryCode}
                      getCountryLabel={(code) => {
                        switch (code) {
                          case "+55": return t('countries.brazil')
                          case "+1": return t('countries.unitedStates')
                          default: return code
                        }
                      }}
                      required
                      disabled={isSendingOTP}
                      autoFocus
                    />
                  </Field>

                  {turnstileRequired && (
                    <div className="flex justify-center">
                      <TurnstileWidget
                        siteKey={turnstileSiteKey}
                        onSuccess={setTurnstileToken}
                        onExpired={() => setTurnstileToken(null)}
                        theme="auto"
                        size="normal"
                      />
                    </div>
                  )}

                  <Field>
                    <Button
                      type="submit"
                      disabled={isSendingOTP || (turnstileRequired && !turnstileToken)}
                    >
                      {isSendingOTP ? t('otp.sendingCode') : t('otp.sendCode')}
                    </Button>
                  </Field>
                </>
              ) : (
                <>
                  <Field>
                    <FieldLabel htmlFor="otp" className="text-center">
                      {t('otp.codePlaceholder')}
                    </FieldLabel>
                    <div className="flex justify-center">
                      <InputOTP
                        maxLength={6}
                        value={otpCode}
                        onChange={(value) => setOtpCode(value)}
                        disabled={isLoading}
                      >
                        <InputOTPGroup>
                          <InputOTPSlot index={0} />
                          <InputOTPSlot index={1} />
                          <InputOTPSlot index={2} />
                        </InputOTPGroup>
                        <InputOTPSeparator />
                        <InputOTPGroup>
                          <InputOTPSlot index={3} />
                          <InputOTPSlot index={4} />
                          <InputOTPSlot index={5} />
                        </InputOTPGroup>
                      </InputOTP>
                    </div>
                  </Field>

                  <Field>
                    <Button type="submit" disabled={isLoading || otpCode.length !== 6}>
                      {isLoading ? (
                        <><Spinner />{t('otp.verifying')}</>
                      ) : (
                        t('otp.verifyCode')
                      )}
                    </Button>
                  </Field>

                  {turnstileRequired && (
                    <div className="flex flex-col items-center gap-2">
                      <FieldDescription className="text-center text-muted-foreground">
                        {t('otp.turnstileForResend')}
                      </FieldDescription>
                      <TurnstileWidget
                        siteKey={turnstileSiteKey}
                        onSuccess={setTurnstileToken}
                        onExpired={() => setTurnstileToken(null)}
                        theme="auto"
                        size="normal"
                      />
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-2">
                    <Field>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleBackToPhone}
                        disabled={isLoading}
                        className="w-full"
                      >
                        {t('otp.resendCode')}
                      </Button>
                    </Field>
                    <Field>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={(e) => {
                          void handleSendOTP(e, alternateSendViaSmsForResend())
                        }}
                        disabled={
                          isLoading ||
                          isSendingOTP ||
                          (turnstileRequired && !turnstileToken) ||
                          didNotReceiveCooldownSec > 0
                        }
                        className="w-full"
                      >
                        {isSendingOTP
                          ? t('otp.sendingCode')
                          : `${t('otp.didNotReceive')}${
                              didNotReceiveCooldownSec > 0
                                ? ` ${t('otp.didNotReceiveCountdown', { seconds: didNotReceiveCooldownSec })}`
                                : ''
                            }`}
                      </Button>
                    </Field>
                  </div>
                </>
              )}
            </FieldGroup>
          </form>
        </TabsContent>

        {/* Identifier + password tab */}
        {!whatsappOnly && (
          <TabsContent value="identifier" className="mt-4">
            <form onSubmit={handleIdentifierLogin}>
              <FieldGroup>
                {identifierError && (
                  <div className="rounded-md bg-destructive/15 p-3 text-sm text-destructive">
                    {identifierError}
                  </div>
                )}

                <Field>
                  <FieldLabel htmlFor="identifier">{t('signin.identifierLabel')}</FieldLabel>
                  <Input
                    id="identifier"
                    type="text"
                    placeholder={t('signin.identifierPlaceholder')}
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                    required
                    disabled={identifierLoading}
                    autoComplete="username"
                  />
                </Field>

                <Field>
                  <FieldLabel htmlFor="password">{t('signin.passwordLabel')}</FieldLabel>
                  <Input
                    id="password"
                    type="password"
                    placeholder={t('signin.passwordPlaceholder')}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={identifierLoading}
                    autoComplete="current-password"
                  />
                </Field>

                {turnstileRequired && (
                  <div className="flex justify-center">
                    <TurnstileWidget
                      siteKey={turnstileSiteKey}
                      onSuccess={setIdentifierTurnstileToken}
                      onExpired={() => setIdentifierTurnstileToken(null)}
                      theme="auto"
                      size="normal"
                    />
                  </div>
                )}

                <Field>
                  <Button
                    type="submit"
                    disabled={identifierLoading || !identifier.trim() || !password || (turnstileRequired && !identifierTurnstileToken)}
                  >
                    {identifierLoading ? (
                      <><Spinner />{t('signin.identifierSigningIn')}</>
                    ) : (
                      t('signin.identifierSignIn')
                    )}
                  </Button>
                </Field>
              </FieldGroup>
            </form>
          </TabsContent>
        )}
      </Tabs>

      <FieldDescription className="px-6 text-center">
        {t('signin.terms')} <a href="#">{t('signin.termsLink')}</a>{" "}
        {t('signin.and')} <a href="#">{t('signin.privacyLink')}</a>.
      </FieldDescription>
      <div className="flex justify-center">
        <LandingLanguageSwitcher />
      </div>
    </div>
  )
}
